import type {
  OffSiteLocationID,
  QuestionnaireAnswers,
  QuestionnaireOffSiteLocation,
  WasteTransfer,
} from '../../types/questionnaires.ts';
import {
  formRBasisFromTransfers,
  type FormRTransferLocationRegistrar,
  type ReleaseByChemicalAndFormRLine,
  transfersToXmlCongeners,
} from './form-r-utils.ts';
import type { CasOrCategory } from '../../types/db.ts';
import type { TransferComputed } from '../releases-calculate.ts';
import type { TRIWasteManagementTypeCodeType } from '../../types/form-r.ts';
import { metalsSet } from '../chemical-molecular-weight-caclulations.ts';
import type { Warning } from '../../types/tri.ts';

interface WasteTypeDescriptor {
  value: TRIWasteManagementTypeCodeType;
  label: string;
  metalsAllowed: boolean;
}

export const formRWasteTypes: {
  Disposal: WasteTypeDescriptor[];
  Treatment: WasteTypeDescriptor[];
  'Energy Recovery': WasteTypeDescriptor[];
  Recycling: WasteTypeDescriptor[];
} = {
  Disposal: [
    { value: 'M10', label: 'Storage Only', metalsAllowed: true },
    {
      value: 'M41',
      label: 'Solidification/Stabilization - Metals and Metal Category Compounds only',
      metalsAllowed: true,
    },
    {
      value: 'M62',
      label: 'Wastewater Treatment (Excluding POTW) - Metals and Metal Category Compounds only',
      metalsAllowed: true,
    },
    { value: 'M64', label: 'Other Landfills', metalsAllowed: true },
    { value: 'M65', label: 'RCRA Subtitle C Landfills', metalsAllowed: true },
    { value: 'M66', label: 'Subtitle C Surface Impoundments', metalsAllowed: true },
    { value: 'M67', label: 'Other Surface Impoundments', metalsAllowed: true },
    { value: 'M73', label: 'Land Treatment', metalsAllowed: true },
    { value: 'M79', label: 'Other Land Disposal', metalsAllowed: true },
    { value: 'M81', label: 'Underground Injection to Class I Wells', metalsAllowed: true },
    { value: 'M82', label: 'Underground Injection to Class II-V Wells', metalsAllowed: true },
    { value: 'M90', label: 'Other Off-Site Management', metalsAllowed: true },
    { value: 'M94', label: 'Transfer to Waste Broker - Disposal', metalsAllowed: true },
    { value: 'M99', label: 'Management Method Unknown', metalsAllowed: true },
  ],

  Treatment: [
    { value: 'M40', label: 'Solidification/Stabilization', metalsAllowed: false },
    { value: 'M50', label: 'Incineration/Thermal Treatment', metalsAllowed: false },
    { value: 'M54', label: 'Incineration/Insignificant Fuel Value', metalsAllowed: false },
    { value: 'M61', label: 'Wastewater Treatment (Excluding POTW)', metalsAllowed: false },
    { value: 'M69', label: 'Other Waste Treatment', metalsAllowed: false },
    { value: 'M95', label: 'Transfer to Waste Broker - Waste Treatment', metalsAllowed: false },
  ],

  'Energy Recovery': [
    { value: 'M56', label: 'Energy Recovery', metalsAllowed: false },
    { value: 'M92', label: 'Transfer to Waste Broker - Energy Recovery', metalsAllowed: false },
  ],

  Recycling: [
    { value: 'M20', label: 'Solvents/Organics Recovery', metalsAllowed: false },
    { value: 'M24', label: 'Metals Recovery – Metals and Metal Category Compounds only', metalsAllowed: true },
    { value: 'M26', label: 'Other Reuse or Recovery', metalsAllowed: true },
    { value: 'M28', label: 'Acid Regeneration', metalsAllowed: false },
    { value: 'M93', label: 'Transfer to Waste Broker - Recycling', metalsAllowed: true },
  ],
};
const formRWasteTypesFlat = [
  ...formRWasteTypes.Disposal,
  ...formRWasteTypes.Treatment,
  ...formRWasteTypes['Energy Recovery'],
  ...formRWasteTypes.Recycling,
];

export function formRSection62OffSiteLocationReleases({
  chemicalName,
  casOrCategory,
  sectionSixTwoReleases,
  wasteTransfers,
  offSiteLocations,
  registerTransferLocation,
  warnings,
}: {
  chemicalName: string;
  casOrCategory: CasOrCategory;
  sectionSixTwoReleases: ReleaseByChemicalAndFormRLine | undefined;
  wasteTransfers: QuestionnaireAnswers['wasteTransfers'];
  offSiteLocations: QuestionnaireAnswers['offSiteLocations'];
  registerTransferLocation: FormRTransferLocationRegistrar;
  warnings: Warning[];
}): void {
  let pushedReleases = false;

  if (sectionSixTwoReleases) {
    const transfersByOffSiteLocationIDAndType = new Map<
      `${OffSiteLocationID}-${TRIWasteManagementTypeCodeType}`,
      {
        totalOffSiteLocationAmount: number;
        offSiteLocation: QuestionnaireOffSiteLocation;
        transferInputs: WasteTransfer[];
        transferCalcs: TransferComputed[];
        wasteType: TRIWasteManagementTypeCodeType;
      }
    >();

    for (const transferCalc of sectionSixTwoReleases.transfers) {
      if (transferCalc.amountReleased === 0) {
        continue;
      }

      pushedReleases = true;

      let transferInput = wasteTransfers[transferCalc.transferID];
      if (!transferInput || transferInput.type !== 'offSiteLocation') {
        warnings.push({
          warningID: 'TW1012',
          type: 'questionnaire',
          subType: transferCalc.transferID,
          casOrCategory,
          message: `${chemicalName} (${casOrCategory}), we were unable to find the associated transfer details from the questionnaire (${transferCalc.transferID}).`,
          blocksPriorYearSection8Calculation: true,
        });
        transferInput = {
          transferID: transferCalc.transferID,
          wasteStreamID: 'waste_stream_missing',
          allocationOrAnswer: '',
          type: 'offSiteLocation',
          offSiteLocationID: 'off_site_missing',
          wasteType: '',
        };
      }

      let offSiteLocation = offSiteLocations[transferInput.offSiteLocationID as OffSiteLocationID];
      if (!offSiteLocation) {
        warnings.push({
          warningID: 'TW1013',
          type: 'questionnaire',
          subType: transferCalc.transferID,
          casOrCategory,
          message: `${chemicalName} (${casOrCategory}), we were unable to find the associated off-site location details from the questionnaire (${transferInput.offSiteLocationID}).`,
          blocksPriorYearSection8Calculation: true,
        });
        offSiteLocation = {
          offSiteLocationID: transferInput.offSiteLocationID as OffSiteLocationID,
          name: 'UNKNOWN OFF-SITE LOCATION',
          address: 'UNKNOWN ADDRESS',
          city: 'UNKNOWN CITY',
          county: 'UNKNOWN ZIP',
          state: 'UNKNOWN STATE',
          zip: '00000',
          province: 'UNKNOWN PROVINCE',
          country: 'UNKNOWN COUNTRY',
          epaID: '0',
          rcraNum: '0',
          isControlledByFacilityOrParentCompany: false,
        };
      }

      if (!transferInput.wasteType) {
        warnings.push({
          warningID: 'TW1014',
          type: 'questionnaire',
          subType: transferInput.transferID,
          casOrCategory,
          message: `${chemicalName} (${casOrCategory}), we were unable to find the associated waste type for the off-site location "${offSiteLocation.name}" (${transferInput.offSiteLocationID}).`,
          blocksPriorYearSection8Calculation: true,
        });
      }

      const wasteType = transferInput.wasteType || 'M99';
      const key = `${offSiteLocation.offSiteLocationID}-${wasteType}` as const;
      if (!transfersByOffSiteLocationIDAndType.has(key)) {
        const typeDescriptor = formRWasteTypesFlat.find((t) => t.value === wasteType);
        if (metalsSet.has(casOrCategory) && typeDescriptor?.metalsAllowed === false) {
          warnings.push({
            warningID: 'TW1015',
            type: 'questionnaire',
            subType: transferInput.transferID,
            casOrCategory,
            message: `${chemicalName} (${casOrCategory}), the waste type code "${wasteType} - ${typeDescriptor.label}" was specified, which is not allowed for metals.`,
            blocksPriorYearSection8Calculation: true,
          });
        }

        transfersByOffSiteLocationIDAndType.set(key, {
          offSiteLocation,
          totalOffSiteLocationAmount: 0,
          transferCalcs: [],
          transferInputs: [],
          wasteType,
        });
      }

      const accumulated = transfersByOffSiteLocationIDAndType.get(key)!;
      accumulated.totalOffSiteLocationAmount += transferCalc.amountReleased;
      accumulated.transferInputs.push(transferInput);
      accumulated.transferCalcs.push(transferCalc);
    }

    for (const {
      offSiteLocation,
      totalOffSiteLocationAmount,
      transferCalcs,
      wasteType,
    } of transfersByOffSiteLocationIDAndType.values()) {
      const transferLocation = registerTransferLocation(offSiteLocation);

      transferLocation.TransferQuantity.push({
        TransferSequenceNumber: transferLocation.TransferQuantity.length + 1,
        TransferWasteQuantity: {
          Quantity: {
            choice: 0,
            value: {
              WasteQuantityMeasure: totalOffSiteLocationAmount,
              WasteQuantityCatastrophicMeasure: null,
            },
          },
          QuantityBasis: {
            choice: 'QuantityBasisEstimationCode',
            value: formRBasisFromTransfers(transferCalcs, {
              chemicalName,
              casOrCategory,
              formRLine: '6.2',
              warnings,
            }),
          },
          ToxicEquivalencyIdentification: transfersToXmlCongeners(casOrCategory, '6.2', transferCalcs, warnings),
          NonProductionWasteQuantityMeasure: null, // TODO: 6. Form R - May need to add support for Section 8.8 at some point.
        },
        WasteManagementTypeCode: wasteType,
      });
    }
  }

  if (!pushedReleases) {
    // This is unintuitive but is how TRI-MEweb marked N/A via manual entry:
    registerTransferLocation({
      TransferLocationSequenceNumber: -1, // registerTransferLocation will update this.
      FacilitySiteName: 'NA',
      POTWIndicator: false,

      // Leave these out since we're marking N/A:
      LocationAddress: null,
      TransferQuantity: [],
      ControlledLocationIndicator: null,
      RCRAIdentificationNumber: null,
      EPARegistryIdentification: null,
    });
  }
}
